import React from "react";
import { MessageBubble } from "./MessageBubble";
import { InstanceDisplay } from "./InstanceDisplay";

interface TraceAgentsViewProps {
  traceData: Record<string, any> | null;
  instanceScores?: Record<string, { score: number | null; explanation: string | null }>;
}

/**
 * Presentational component for displaying trace agents and their interactions.
 * 
 * Primary schema: MultiAgentTrace from align.common.api_types
 *   trace: {
 *     root_agent_instance_id: string
 *     agent_instance_by_id: { [id: string]: AgentInstance }
 *   }
 *   Where AgentInstance {
 *     id: string
 *     name: string
 *     interactions: LLMInteraction[]
 *   }
 * 
 * Also supports old schema (agents array) for backward compatibility.
 * Used within Breakdown section, so no card wrapper.
 */
export function TraceAgentsView({ traceData, instanceScores = {} }: TraceAgentsViewProps) {
  if (!traceData) {
    return null;
  }

  // Check for MultiAgentTrace schema (trace.agent_instance_by_id)
  const agentInstanceById = traceData.trace?.agent_instance_by_id;
  const rootInstanceId = traceData.trace?.root_agent_instance_id;

  // Check for old schema (agents array)
  const agentsArray = traceData.agents;

  // Check for single AgentInstance (reward API format: { id, name, interactions })
  const singleInstance =
    traceData.id != null &&
    traceData.name != null &&
    Array.isArray(traceData.interactions) &&
    traceData.interactions.length > 0;

  // Handle single agent instance (reward format)
  if (singleInstance) {
    return (
      <div className="space-y-6">
        <InstanceDisplay
          instance={traceData}
          instanceIndex={0}
          isRoot={true}
          instanceScore={instanceScores[traceData.id]}
        />
      </div>
    );
  }

  // Handle MultiAgentTrace schema (trace.agent_instance_by_id)
  if (agentInstanceById && typeof agentInstanceById === "object") {
    const instances = Object.values(agentInstanceById) as any[];
    
    if (instances.length === 0) {
      return null;
    }

    return (
      <div className="space-y-6">
        {instances.map((instance: any, instanceIndex: number) => (
          <InstanceDisplay
            key={instance.id || instanceIndex}
            instance={instance}
            instanceIndex={instanceIndex}
            isRoot={instance.id === rootInstanceId}
            instanceScore={instanceScores[instance.id]}
          />
        ))}
      </div>
    );
  }

  // Handle old schema (agents array)
  if (agentsArray && Array.isArray(agentsArray) && agentsArray.length > 0) {
    return (
      <div className="space-y-6">
        {agentsArray.map((agent: any, agentIndex: number) => (
          <div key={agentIndex} className="border border-border rounded-lg p-4">
            <h4 className="text-sm font-semibold text-muted-foreground mb-3">
              Agent: {agent.agent_name || `Agent ${agentIndex + 1}`}
            </h4>
            {agent.instances && Array.isArray(agent.instances) && agent.instances.length > 0 ? (
              <div className="space-y-4">
                {agent.instances.map((instance: any, instanceIndex: number) => (
                  <div key={instanceIndex} className="bg-muted rounded-lg p-4">
                    <div className="mb-3">
                      <span className="text-xs font-medium text-muted-foreground">
                        Instance ID:{" "}
                        <code className="bg-muted px-2 py-0.5 rounded">
                          {instance.instance_id}
                        </code>
                      </span>
                    </div>
                    {instance.interactions &&
                    Array.isArray(instance.interactions) &&
                    instance.interactions.length > 0 ? (
                      <div className="space-y-3">
                        {instance.interactions.map((interaction: any, interactionIndex: number) => (
                          <MessageBubble
                            key={interactionIndex}
                            role={interaction.role || "unknown"}
                            content={interaction.content}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No interactions found</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No instances found</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  return null;
}
